/**
 * Pure helpers for the portfolio page (JARVIS-126) that need student data:
 * turning a project's steps into ArtworkWall items for the drill-in, and the
 * cover rotation lookup. The arrangement/lane/tag logic lives in
 * PortfolioManagement/portfolioArrangement.ts.
 */
import dayjs from "dayjs"

import { type ArtworkWallItem } from "#components/ArtworkWall/artworkWallTypes"
import {
  getProjectCoverStage,
  type PortfolioLaneChip,
} from "#components/PortfolioManagement/portfolioArrangement"
import {
  type PortfolioMockProject,
  type PortfolioMockStage,
} from "#components/PortfolioManagement/portfolioUiTypes"
import {
  type StudentArtwork,
  type StudentConstructor,
} from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"

/**
 * A step with no artwork record still has to render on the wall, so it gets a
 * minimal StudentArtwork built from the stage. `classId`/`semester` are
 * blank: the wall never reads them, and this object is never persisted.
 */
export const syntheticArtworkForStage = (
  stage: PortfolioMockStage,
  projectId: string
): StudentArtwork => ({
  id: stage.id as Uuid,
  semester: "",
  classId: "" as Uuid,
  projectId: projectId as Uuid,
  filename: stage.label,
  path: "",
  url: stage.imageUrl,
  timeCreated: dayjs(stage.createdAt ?? stage.lastActivityAt ?? 0).valueOf(),
})

/**
 * The drill-in: one wall tile per step that has an image, badged
 * "Step N of M" by position in the FULL step list, so a step without an image
 * still holds its number. Real artwork records are preferred (rotation and
 * renditions come from them); a step with none gets a synthetic one.
 */
export const buildProjectWallItems = (
  project: PortfolioMockProject,
  student: Pick<StudentConstructor, "id" | "name" | "artwork"> | null
): ArtworkWallItem[] => {
  const total = project.stages.length
  const items: ArtworkWallItem[] = []
  project.stages.forEach((stage, index) => {
    const linked = stage.artworkId
      ? student?.artwork?.[stage.artworkId as Uuid]
      : undefined
    const artwork =
      linked ??
      (stage.imageUrl?.trim()
        ? syntheticArtworkForStage(stage, project.id)
        : null)
    if (!artwork) return
    items.push({
      id: stage.id,
      artwork,
      studentId: (student?.id ?? "") as Uuid,
      studentName: student?.name,
      caption: `${project.title} — ${stage.label}`,
      badge: `Step ${index + 1} of ${total}`,
    })
  })
  return items
}

/** Rotation for the tile cover: the cover step's artwork record, if any. */
export const getProjectCoverRotation = (
  project: PortfolioMockProject,
  artworkById: Record<string, StudentArtwork> | undefined
): number => {
  const stage = getProjectCoverStage(project)
  if (!stage?.artworkId) return 0
  return artworkById?.[stage.artworkId]?.rotation ?? 0
}

export const formatLastEdited = (ms: number): string =>
  ms > 0 ? dayjs(ms).format("MMM D, YYYY") : "never"

/** The chip a drop landed on, if the drop target was a category chip. */
export const isCategoryChip = (
  chip: PortfolioLaneChip
): chip is Exclude<PortfolioLaneChip, "priority"> => chip !== "priority"
